import { useEffect, useRef, useState } from 'react';
import type { NonConformity } from '@obraplan/core/domain/quality/non-conformity';
import { isAreaUnitCode } from '@obraplan/core/domain/schedule/unit-of-measure';
import type { DayQualityFrontView } from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { AreaMeasurementButton } from './AreaMeasurementDialog';
import type { RoomOfDay } from './nc-places';
import { QualityVerificationStep } from './QualityVerificationStep';
import {
  frontDraftToInput,
  initialFrontDraft,
  isFrontDraftComplete,
  NEW_MEASUREMENT_OWNER,
  parseQuantityText,
  pendingPhotos,
  type FrontDraft,
  type PendingNonConformityPhotos,
  type VerificationInput,
} from './quality-verification-draft';

/**
 * A checklist de **uma** frente, num modal (Fase 20).
 *
 * É o mesmo passo da medição da conferência (`QualityVerificationStep`), sem cópia — o
 * que muda é a moldura: aqui ele abre sozinho, para a atividade que acabou de receber
 * produção e horas no RDO, e fecha com um botão só. Quando a frente é medida e a
 * quantidade ainda não foi dita, o modal a pergunta primeiro; a checklist vem embaixo.
 *
 * A frente sem serviço cadastrado não tem checklist, e o modal vira só a pergunta da
 * quantidade — sem passo vazio no meio.
 */
export interface WorkQualityDialogProps {
  open: boolean;
  title: string;
  description: string;
  /** `undefined` enquanto a leitura da frente não chegou — o modal espera. */
  front: DayQualityFrontView | undefined;
  /** O serviço escolhido no formulário. Com ele, o modal não pergunta de novo qual foi. */
  serviceId?: string;
  nonConformities: readonly NonConformity[];
  /** De quem é cada NC do dia — "Rafael Souza" (Fase 37). */
  nonConformityOwners?: Readonly<Record<string, string>> | undefined;
  /** As NCs abertas de outros dias — o aviso da remedição (Fase 37, D10). */
  pendingNonConformities?: readonly NonConformity[] | undefined;
  /**
   * As peças e os cômodos que o lançamento marcou agora, antes de gravar (Fase 37, D9): a NC
   * pode apontá-los.
   */
  draftPlaces?:
    | {
        readonly pieces?: readonly string[] | undefined;
        readonly rooms?: readonly RoomOfDay[] | undefined;
      }
    | undefined;
  /** Presente = o modal também pergunta a quantidade executada. */
  quantity?:
    | {
        readonly value: string;
        readonly unitName: string;
        /** O código da unidade: no m², o modal oferece a produção pela medida (RN-379). */
        readonly unitCode?: string | undefined;
        readonly unitDecimals?: number | undefined;
        readonly onChange: (value: string) => void;
      }
    | undefined;
  /**
   * Presente = o lançamento pode passar da jornada, e a exceção pede motivo (RN-045). Fica
   * no modal, e não só no formulário, para que a recusa de capacidade seja resolvida sem
   * fechar a checklist já respondida.
   */
  justification?:
    { readonly value: string; readonly onChange: (value: string) => void } | undefined;
  confirmLabel: string;
  isPending: boolean;
  errorMessage?: string | undefined;
  onOpenChange: (open: boolean) => void;
  /**
   * As verificações, e as fotos escolhidas nos itens reprovados — que sobem depois de gravar,
   * quando a NC já tem número (Fase 37, RN-479).
   */
  onConfirm: (
    verifications: readonly VerificationInput[],
    photos: readonly PendingNonConformityPhotos[],
  ) => void;
}

export function WorkQualityDialog({
  open,
  title,
  description,
  front,
  serviceId,
  nonConformities,
  nonConformityOwners,
  pendingNonConformities,
  draftPlaces,
  quantity,
  justification,
  confirmLabel,
  isPending,
  errorMessage,
  onOpenChange,
  onConfirm,
}: WorkQualityDialogProps) {
  const [draft, setDraft] = useState<FrontDraft>({});

  // O rascunho nasce do que já foi verificado hoje, uma vez por abertura e por frente —
  // e não a cada render, senão a resposta de quem está respondendo seria apagada.
  const chaveAtual = open && front !== undefined ? front.activityId : undefined;
  const chaveAnterior = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (chaveAtual === chaveAnterior.current) return;
    chaveAnterior.current = chaveAtual;
    if (front !== undefined && chaveAtual !== undefined) {
      // O lançamento cria a medição: nenhuma NC do dia é dela ainda (Fase 37, RN-474).
      setDraft(initialFrontDraft(front, nonConformities, serviceId, () => NEW_MEASUREMENT_OWNER));
    }
  }, [chaveAtual, front, nonConformities]);

  const temChecklist = front !== undefined && front.services.length > 0;
  const completo = front !== undefined && (!temChecklist || isFrontDraftComplete(front, draft));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Coluna flex: a checklist rola por dentro e o botão de lançar fica à vista no celular. */}
      <DialogContent className="flex max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {quantity !== undefined && (
          <Field
            label={`Quantidade executada (${quantity.unitName})`}
            htmlFor="rdo-qualidade-quantidade"
            hint="Em branco = ainda não medida."
          >
            <div className="flex gap-2">
              <Input
                id="rdo-qualidade-quantidade"
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                value={quantity.value}
                disabled={isPending}
                onChange={(event) => quantity.onChange(event.target.value)}
              />
              {isAreaUnitCode(quantity.unitCode) && (
                <AreaMeasurementButton
                  unitName={quantity.unitName}
                  decimals={quantity.unitDecimals ?? 2}
                  disabled={isPending}
                  onApply={(total) => quantity.onChange(String(total))}
                />
              )}
            </div>
          </Field>
        )}

        {front === undefined ? (
          <p className="text-sm text-(--color-content-muted)">Carregando a checklist da frente…</p>
        ) : temChecklist ? (
          <div className="relative flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
            <QualityVerificationStep
              {...(serviceId === undefined ? {} : { serviceId })}
              front={front}
              draft={draft}
              nonConformities={nonConformities}
              disabled={isPending}
              nonConformityOwners={nonConformityOwners}
              pendingNonConformities={pendingNonConformities}
              measurementOf={() => {
                const medido =
                  quantity === undefined ? undefined : parseQuantityText(quantity.value);
                return {
                  ownerKey: NEW_MEASUREMENT_OWNER,
                  measured: typeof medido === 'number' ? medido : undefined,
                  unitName: quantity?.unitName,
                  unitCode: quantity?.unitCode,
                  unitDecimals: quantity?.unitDecimals,
                  draftPieces: draftPlaces?.pieces,
                  draftRooms: draftPlaces?.rooms,
                };
              }}
              onChange={setDraft}
              onBack={() => onOpenChange(false)}
            />
          </div>
        ) : (
          <p className="text-sm text-(--color-content-muted)">
            A obra não cadastrou serviço para {front.disciplineName}: esta frente não tem checklist,
            e mede como sempre mediu.
          </p>
        )}

        {justification !== undefined && (
          <Field
            label="Justificativa da hora extra"
            htmlFor="rdo-qualidade-justificativa"
            hint="Só é lida se as horas passarem da jornada do dia (RN-045)."
          >
            <Textarea
              id="rdo-qualidade-justificativa"
              rows={2}
              value={justification.value}
              disabled={isPending}
              onChange={(event) => justification.onChange(event.target.value)}
            />
          </Field>
        )}

        {errorMessage !== undefined && (
          <p role="alert" className="text-sm text-(--color-state-risk)">
            {errorMessage}
          </p>
        )}

        <DialogFooter>
          <Button variant="secondary" disabled={isPending} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            disabled={!completo || isPending}
            onClick={() => {
              if (front === undefined) return;
              onConfirm(
                temChecklist ? frontDraftToInput(front, draft) : [],
                temChecklist ? pendingPhotos(front, draft) : [],
              );
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
